// Typed AbortController reasons for the renderer execution pipeline.
// @arch:pure-engine
//
// `AbortController.abort()` without a reason collapses user Stop, watchdog,
// memory shedding and session deletion into the same DOMException("AbortError").
// The DAG then has no truthful cause to surface and used to print the opaque
// `DAG bloqueado (aborted)`.  Keep the transport deliberately tiny: a stable
// string travels through nested AbortControllers and is normalized at the DAG
// boundary. Unknown/legacy aborts remain `aborted` for backwards compatibility.

export const ABORT_REASON = Object.freeze({
  UNKNOWN: 'aborted',
  USER_STOP: 'user_stop',
  RUN_IDLE_TIMEOUT: 'run_idle_timeout',
  TASK_TIMEOUT: 'task_timeout',
  MEMORY_PRESSURE: 'memory_pressure',
  SESSION_DELETED: 'session_deleted',
});

/** @type {Set<string>} */
const KNOWN = new Set(Object.values(ABORT_REASON));

/**
 * Return a stable reason from an AbortSignal (legacy DOMException => aborted).
 * @param {AbortSignal|undefined|null} signal
 * @returns {string}
 */
export function abortReasonOf(signal) {
  const reason = signal?.reason;
  if (typeof reason === 'string' && KNOWN.has(reason)) return reason;
  const code = reason && typeof reason === 'object' ? reason.code : null;
  return typeof code === 'string' && KNOWN.has(code) ? code : ABORT_REASON.UNKNOWN;
}

/**
 * Abort once with a typed reason. Returns true only when this call aborted it.
 * @param {{ signal?: { aborted?: boolean }, abort: (reason?: any) => void }|undefined|null} controller
 * @param {string} [reason]
 * @returns {boolean}
 */
export function abortWithReason(controller, reason = ABORT_REASON.UNKNOWN) {
  if (!controller || controller.signal?.aborted) return false;
  const safeReason = KNOWN.has(reason) ? reason : ABORT_REASON.UNKNOWN;
  try {
    controller.abort(safeReason);
  } catch {
    controller.abort();
  }
  return true;
}
